import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import AppHeader from '../components/ui/AppHeader';
import {
  JourneyData,
  JourneySettings,
  JourneyTrustStats,
  JourneyMedia,
  subscribeToJourneyData,
  saveJourneyStage,
  saveJourneyTrustStats,
  saveJourneySettings,
  saveJourneyMedia,
  publishJourney,
  unpublishJourney,
  uploadJourneyMedia,
  getDefaultJourneyData,
  createEmptyQuestion,
  createEmptyDeliverable,
} from '../utils/journeyFirestore';
import { JourneyStage, JourneyQuestion, JourneyTestimonial, JourneyDeliverable } from '../utils/journeyContent';
import { MediaUploadField } from '../components/journey/MediaUploadField';
import { ModelUploadField } from '../components/journey/ModelUploadField';
import {
  ArrowLeft,
  Eye,
  Globe,
  GlobeLock,
  Save,
  Loader2,
  FileSignature,
  Box,
  ClipboardList,
  PenTool,
  FileText,
  Factory,
  Truck,
  MessageSquare,
  Users,
  Star,
  MapPin,
  Settings,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  GripVertical,
  Upload,
  Image as ImageIcon,
  Video,
  Film,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  BarChart3,
  Quote,
  HelpCircle,
  Package,
  Clock,
  X,
} from 'lucide-react';
import './JourneyManagement.css';

// ─── Stage Icons ────────────────────────────────────────────────────────────

const STAGE_ICONS: Record<string, React.ReactNode> = {
  'design-fee': <FileSignature size={16} />,
  '3d-model': <Box size={16} />,
  'quote': <ClipboardList size={16} />,
  'quote-signed': <PenTool size={16} />,
  '2d-plans': <FileText size={16} />,
  'in-production': <Factory size={16} />,
  'delivery': <Truck size={16} />,
};

type ActiveTab = 'stages' | 'stats' | 'media' | 'settings';

// ─── Main Component ─────────────────────────────────────────────────────────

const JourneyManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState<JourneyData>(getDefaultJourneyData);
  const [activeTab, setActiveTab] = useState<ActiveTab>('stages');
  const [selectedStage, setSelectedStage] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Working copies for editing
  const [editStage, setEditStage] = useState<JourneyStage | null>(null);
  const [editStats, setEditStats] = useState<JourneyTrustStats | null>(null);
  const [editMedia, setEditMedia] = useState<JourneyMedia | null>(null);
  const [editSettings, setEditSettings] = useState<JourneySettings | null>(null);

  // Subscribe to Firestore data
  useEffect(() => {
    const unsub = subscribeToJourneyData((newData) => {
      setData(newData);
      if (!hasUnsavedChanges) {
        setEditStage(newData.stages[selectedStage] || null);
        setEditStats(newData.trustStats);
        setEditMedia(newData.media || null);
        setEditSettings(newData.settings);
      }
    });
    return unsub;
  }, [selectedStage, hasUnsavedChanges]);

  // Sync selected stage
  useEffect(() => {
    if (data.stages[selectedStage]) {
      setEditStage({ ...data.stages[selectedStage] });
      setHasUnsavedChanges(false);
    }
  }, [selectedStage]);

  // Initialize edit state from data (skip when user has unsaved changes)
  useEffect(() => {
    if (hasUnsavedChanges) return;
    setEditStats({ ...data.trustStats });
    setEditMedia(data.media ? { ...data.media } : null);
    setEditSettings({ ...data.settings });
  }, [data.trustStats, data.media, data.settings, hasUnsavedChanges]);

  // ─── Save Handlers ──────────────────────────────────────────────────────

  const showSaved = useCallback((msg: string) => {
    setSaveMessage(msg);
    setTimeout(() => setSaveMessage(''), 3000);
  }, []);

  const handleSaveStage = useCallback(async () => {
    if (!editStage) return;
    setSaving(true);
    try {
      await saveJourneyStage(selectedStage, editStage, user?.id, user?.name || user?.email);
      setHasUnsavedChanges(false);
      showSaved('Stage saved');
    } catch (err) {
      console.error('Save failed:', err);
      showSaved('Save failed — try again');
    }
    setSaving(false);
  }, [editStage, selectedStage, user, showSaved]);

  const handleSaveStats = useCallback(async () => {
    if (!editStats) return;
    setSaving(true);
    try {
      await saveJourneyTrustStats(editStats, user?.id, user?.name || user?.email);
      showSaved('Trust stats saved');
    } catch (err) {
      console.error('Save failed:', err);
      showSaved('Save failed — try again');
    }
    setSaving(false);
  }, [editStats, user, showSaved]);

  const handleSaveMedia = useCallback(async () => {
    if (!editMedia) return;
    setSaving(true);
    try {
      await saveJourneyMedia(editMedia, user?.id, user?.name || user?.email);
      setHasUnsavedChanges(false);
      showSaved('Media saved');
    } catch (err) {
      console.error('Save failed:', err);
      showSaved('Save failed — try again');
    }
    setSaving(false);
  }, [editMedia, user, showSaved]);

  const handleSaveSettings = useCallback(async () => {
    if (!editSettings) return;
    setSaving(true);
    try {
      await saveJourneySettings(editSettings, user?.id, user?.name || user?.email);
      showSaved('Settings saved');
    } catch (err) {
      console.error('Save failed:', err);
      showSaved('Save failed — try again');
    }
    setSaving(false);
  }, [editSettings, user, showSaved]);

  const handlePublish = useCallback(async () => {
    setSaving(true);
    try {
      await publishJourney(user?.id, user?.name || user?.email);
      showSaved('Journey published!');
    } catch (err) {
      console.error('Publish failed:', err);
      showSaved('Publish failed');
    }
    setSaving(false);
  }, [user, showSaved]);

  const handleUnpublish = useCallback(async () => {
    setSaving(true);
    try {
      await unpublishJourney(user?.id, user?.name || user?.email);
      showSaved('Journey unpublished');
    } catch (err) {
      console.error('Unpublish failed:', err);
    }
    setSaving(false);
  }, [user, showSaved]);

  // ─── Stage Field Updaters ───────────────────────────────────────────────

  const updateStageField = useCallback(<K extends keyof JourneyStage>(field: K, value: JourneyStage[K]) => {
    setEditStage(prev => prev ? { ...prev, [field]: value } : null);
    setHasUnsavedChanges(true);
  }, []);

  const updateQuestion = useCallback((index: number, field: keyof JourneyQuestion, value: string) => {
    setEditStage(prev => {
      if (!prev) return null;
      const questions = [...prev.questions];
      questions[index] = { ...questions[index], [field]: value };
      return { ...prev, questions };
    });
    setHasUnsavedChanges(true);
  }, []);

  const addQuestion = useCallback(() => {
    setEditStage(prev => {
      if (!prev) return null;
      return { ...prev, questions: [...prev.questions, createEmptyQuestion()] };
    });
    setHasUnsavedChanges(true);
  }, []);

  const removeQuestion = useCallback((index: number) => {
    setEditStage(prev => {
      if (!prev) return null;
      return { ...prev, questions: prev.questions.filter((_, i) => i !== index) };
    });
    setHasUnsavedChanges(true);
  }, []);

  const moveQuestion = useCallback((index: number, direction: 'up' | 'down') => {
    setEditStage(prev => {
      if (!prev) return null;
      const questions = [...prev.questions];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= questions.length) return prev;
      [questions[index], questions[target]] = [questions[target], questions[index]];
      return { ...prev, questions };
    });
    setHasUnsavedChanges(true);
  }, []);

  const updateTestimonial = useCallback((field: keyof JourneyTestimonial, value: any) => {
    setEditStage(prev => {
      if (!prev) return null;
      return { ...prev, testimonial: { ...prev.testimonial, [field]: value } };
    });
    setHasUnsavedChanges(true);
  }, []);

  const updateDeliverable = useCallback((index: number, field: keyof JourneyDeliverable, value: string) => {
    setEditStage(prev => {
      if (!prev) return null;
      const deliverables = [...prev.deliverables];
      deliverables[index] = { ...deliverables[index], [field]: value };
      return { ...prev, deliverables };
    });
    setHasUnsavedChanges(true);
  }, []);

  const addDeliverable = useCallback(() => {
    setEditStage(prev => {
      if (!prev) return null;
      return { ...prev, deliverables: [...prev.deliverables, createEmptyDeliverable()] };
    });
    setHasUnsavedChanges(true);
  }, []);

  const removeDeliverable = useCallback((index: number) => {
    setEditStage(prev => {
      if (!prev) return null;
      return { ...prev, deliverables: prev.deliverables.filter((_, i) => i !== index) };
    });
    setHasUnsavedChanges(true);
  }, []);

  // ─── Photo Upload ───────────────────────────────────────────────────────

  const photoInputRef = useRef<HTMLInputElement>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const handlePhotoUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editStage) return;
    setUploadingPhoto(true);
    try {
      const url = await uploadJourneyMedia(file, `testimonials/${editStage.id}`);
      updateTestimonial('photoUrl', url);
    } catch (err) {
      console.error('Upload failed:', err);
    }
    setUploadingPhoto(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
  }, [editStage, updateTestimonial]);

  // ─── Keyboard Shortcut ──────────────────────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (activeTab === 'stages') handleSaveStage();
        else if (activeTab === 'stats') handleSaveStats();
        else if (activeTab === 'media') handleSaveMedia();
        else if (activeTab === 'settings') handleSaveSettings();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, handleSaveStage, handleSaveStats, handleSaveMedia, handleSaveSettings]);

  if (!editStage || !editStats || !editSettings) return null;

  return (
    <div className="jm-page">
      <AppHeader />

      {/* ─── Top Bar ─────────────────────────────────────────────────────── */}
      <div className="jm-topbar">
        <div className="jm-topbar-left">
          <button className="jm-back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
          </button>
          <div className="jm-topbar-title">
            <h1>Journey & Trust Portal</h1>
            <span className="jm-topbar-subtitle">Manage the public /journey experience</span>
          </div>
        </div>
        <div className="jm-topbar-right">
          {saveMessage && (
            <span className={`jm-save-indicator ${saveMessage.includes('failed') ? 'is-error' : 'is-success'}`}>
              {saveMessage.includes('failed') ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />}
              {saveMessage}
            </span>
          )}
          {hasUnsavedChanges && (
            <span className="jm-unsaved-badge">Unsaved changes</span>
          )}
          <a
            href="/journey"
            target="_blank"
            rel="noopener noreferrer"
            className="jm-btn jm-btn-ghost"
          >
            <Eye size={15} />
            <span>Preview</span>
          </a>
          {data.settings.isPublished ? (
            <button className="jm-btn jm-btn-outline-danger" onClick={handleUnpublish} disabled={saving}>
              <GlobeLock size={15} />
              <span>Unpublish</span>
            </button>
          ) : (
            <button className="jm-btn jm-btn-primary" onClick={handlePublish} disabled={saving}>
              {saving ? <Loader2 size={15} className="jm-spin" /> : <Globe size={15} />}
              <span>Publish</span>
            </button>
          )}
        </div>
      </div>

      {/* ─── Tab Navigation ──────────────────────────────────────────────── */}
      <div className="jm-tabs">
        <button
          className={`jm-tab ${activeTab === 'stages' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('stages')}
        >
          <MessageSquare size={15} />
          <span>Stages & Content</span>
        </button>
        <button
          className={`jm-tab ${activeTab === 'stats' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          <BarChart3 size={15} />
          <span>Trust Stats</span>
        </button>
        <button
          className={`jm-tab ${activeTab === 'media' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          <Film size={15} />
          <span>Media & Models</span>
        </button>
        <button
          className={`jm-tab ${activeTab === 'settings' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <Settings size={15} />
          <span>Page Settings</span>
        </button>
      </div>

      {/* ─── Content ─────────────────────────────────────────────────────── */}
      <div className="jm-content">

        {/* ── Stages Tab ───────────────────────────────────────────────── */}
        {activeTab === 'stages' && (
          <div className="jm-stages-layout">
            {/* Stage sidebar */}
            <nav className="jm-stage-nav">
              <div className="jm-stage-nav-label">Stages</div>
              {data.stages.map((stage, i) => (
                <button
                  key={stage.id}
                  className={`jm-stage-nav-item ${i === selectedStage ? 'is-active' : ''}`}
                  onClick={() => setSelectedStage(i)}
                >
                  <span className="jm-stage-nav-icon">{STAGE_ICONS[stage.id]}</span>
                  <span className="jm-stage-nav-num">{stage.number}</span>
                  <span className="jm-stage-nav-name">{stage.title}</span>
                </button>
              ))}
            </nav>

            {/* Stage editor */}
            <div className="jm-stage-editor">
              {/* Save bar */}
              <div className="jm-save-bar">
                <div className="jm-save-bar-info">
                  <span className="jm-editing-label">
                    {STAGE_ICONS[editStage.id]}
                    Editing Stage {editStage.number}: {editStage.title}
                  </span>
                  {data.updatedBy && data.updatedAt && (
                    <span className="jm-last-edit">
                      Last edit by {data.updatedBy} · {data.updatedAt.toLocaleDateString()}
                    </span>
                  )}
                </div>
                <button
                  className="jm-btn jm-btn-save"
                  onClick={handleSaveStage}
                  disabled={saving || !hasUnsavedChanges}
                >
                  {saving ? <Loader2 size={15} className="jm-spin" /> : <Save size={15} />}
                  <span>{saving ? 'Saving…' : 'Save Stage'}</span>
                </button>
              </div>

              {/* ── Stage Details Card ─────────────────────────────────── */}
              <div className="jm-card">
                <div className="jm-card-header">
                  <Settings size={16} />
                  <h3>Stage Details</h3>
                </div>
                <div className="jm-card-body">
                  <div className="jm-field-row">
                    <div className="jm-field">
                      <label className="jm-label">Title</label>
                      <input
                        className="jm-input"
                        value={editStage.title}
                        onChange={e => updateStageField('title', e.target.value)}
                        placeholder="Stage title"
                      />
                    </div>
                    <div className="jm-field">
                      <label className="jm-label">Duration</label>
                      <input
                        className="jm-input"
                        value={editStage.duration}
                        onChange={e => updateStageField('duration', e.target.value)}
                        placeholder="e.g. 2–3 weeks"
                      />
                    </div>
                  </div>
                  <div className="jm-field">
                    <label className="jm-label">Subtitle</label>
                    <input
                      className="jm-input"
                      value={editStage.subtitle}
                      onChange={e => updateStageField('subtitle', e.target.value)}
                      placeholder="Short description of this stage"
                    />
                  </div>
                </div>
              </div>

              {/* ── Lead Q&A Card ──────────────────────────────────────── */}
              <div className="jm-card">
                <div className="jm-card-header">
                  <HelpCircle size={16} />
                  <h3>Lead Question</h3>
                  <span className="jm-card-hint">The #1 question prospects ask at this stage</span>
                </div>
                <div className="jm-card-body">
                  <div className="jm-field">
                    <label className="jm-label">Question</label>
                    <input
                      className="jm-input jm-input-lg"
                      value={editStage.leadQuestion}
                      onChange={e => updateStageField('leadQuestion', e.target.value)}
                      placeholder="What do prospects ask at this stage?"
                    />
                  </div>
                  <div className="jm-field">
                    <label className="jm-label">Answer</label>
                    <textarea
                      className="jm-textarea"
                      value={editStage.leadAnswer}
                      onChange={e => updateStageField('leadAnswer', e.target.value)}
                      placeholder="Write a clear, confidence-building answer…"
                      rows={5}
                    />
                    <span className="jm-field-hint">{editStage.leadAnswer.length} characters</span>
                  </div>
                </div>
              </div>

              {/* ── Additional Q&As Card ───────────────────────────────── */}
              <div className="jm-card">
                <div className="jm-card-header">
                  <MessageSquare size={16} />
                  <h3>Additional Questions ({editStage.questions.length})</h3>
                  <button className="jm-btn jm-btn-sm" onClick={addQuestion}>
                    <Plus size={14} />
                    <span>Add</span>
                  </button>
                </div>
                <div className="jm-card-body jm-card-body-flush">
                  {editStage.questions.length === 0 && (
                    <div className="jm-empty">
                      <HelpCircle size={20} />
                      <p>No questions yet. Add one to get started.</p>
                    </div>
                  )}
                  {editStage.questions.map((q, i) => (
                    <div className="jm-qa-item" key={q.id}>
                      <div className="jm-qa-item-controls">
                        <button
                          className="jm-icon-btn"
                          onClick={() => moveQuestion(i, 'up')}
                          disabled={i === 0}
                          title="Move up"
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          className="jm-icon-btn"
                          onClick={() => moveQuestion(i, 'down')}
                          disabled={i === editStage.questions.length - 1}
                          title="Move down"
                        >
                          <ChevronDown size={14} />
                        </button>
                        <button
                          className="jm-icon-btn jm-icon-btn-danger"
                          onClick={() => removeQuestion(i)}
                          title="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="jm-qa-item-fields">
                        <input
                          className="jm-input"
                          value={q.question}
                          onChange={e => updateQuestion(i, 'question', e.target.value)}
                          placeholder="Question…"
                        />
                        <textarea
                          className="jm-textarea jm-textarea-sm"
                          value={q.answer}
                          onChange={e => updateQuestion(i, 'answer', e.target.value)}
                          placeholder="Answer…"
                          rows={3}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Testimonial Card ───────────────────────────────────── */}
              <div className="jm-card">
                <div className="jm-card-header">
                  <Quote size={16} />
                  <h3>Client Testimonial</h3>
                </div>
                <div className="jm-card-body">
                  <div className="jm-field-row">
                    <div className="jm-field">
                      <label className="jm-label">Client Name</label>
                      <input
                        className="jm-input"
                        value={editStage.testimonial.name}
                        onChange={e => updateTestimonial('name', e.target.value)}
                        placeholder="e.g. The Smith Family"
                      />
                    </div>
                    <div className="jm-field">
                      <label className="jm-label">Location</label>
                      <input
                        className="jm-input"
                        value={editStage.testimonial.location}
                        onChange={e => updateTestimonial('location', e.target.value)}
                        placeholder="e.g. Stowe, Vermont"
                      />
                    </div>
                  </div>
                  <div className="jm-field-row">
                    <div className="jm-field">
                      <label className="jm-label">Project Type</label>
                      <input
                        className="jm-input"
                        value={editStage.testimonial.projectType}
                        onChange={e => updateTestimonial('projectType', e.target.value)}
                        placeholder="e.g. Mountain Retreat"
                      />
                    </div>
                    <div className="jm-field jm-field-sm">
                      <label className="jm-label">Year</label>
                      <input
                        className="jm-input"
                        type="number"
                        value={editStage.testimonial.year}
                        onChange={e => updateTestimonial('year', parseInt(e.target.value) || new Date().getFullYear())}
                      />
                    </div>
                  </div>
                  <div className="jm-field">
                    <label className="jm-label">Quote</label>
                    <textarea
                      className="jm-textarea"
                      value={editStage.testimonial.quote}
                      onChange={e => updateTestimonial('quote', e.target.value)}
                      placeholder="What did this client say about this stage of their journey?"
                      rows={4}
                    />
                  </div>

                  {/* Photo upload */}
                  <div className="jm-field">
                    <label className="jm-label">Client Photo</label>
                    <div className="jm-media-upload">
                      {editStage.testimonial.photoUrl ? (
                        <div className="jm-media-preview">
                          <img src={editStage.testimonial.photoUrl} alt="Client" />
                          <div className="jm-media-preview-actions">
                            <button
                              className="jm-icon-btn"
                              onClick={() => photoInputRef.current?.click()}
                              title="Replace"
                            >
                              <Upload size={14} />
                            </button>
                            <button
                              className="jm-icon-btn jm-icon-btn-danger"
                              onClick={() => updateTestimonial('photoUrl', '')}
                              title="Remove"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          className="jm-upload-zone"
                          onClick={() => photoInputRef.current?.click()}
                          disabled={uploadingPhoto}
                        >
                          {uploadingPhoto ? (
                            <Loader2 size={20} className="jm-spin" />
                          ) : (
                            <>
                              <ImageIcon size={20} />
                              <span>Upload photo</span>
                            </>
                          )}
                        </button>
                      )}
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        hidden
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Deliverables Card ──────────────────────────────────── */}
              <div className="jm-card">
                <div className="jm-card-header">
                  <Package size={16} />
                  <h3>Deliverables ({editStage.deliverables.length})</h3>
                  <button className="jm-btn jm-btn-sm" onClick={addDeliverable}>
                    <Plus size={14} />
                    <span>Add</span>
                  </button>
                </div>
                <div className="jm-card-body jm-card-body-flush">
                  {editStage.deliverables.length === 0 && (
                    <div className="jm-empty">
                      <Package size={20} />
                      <p>No deliverables yet.</p>
                    </div>
                  )}
                  {editStage.deliverables.map((d, i) => (
                    <div className="jm-deliverable-item" key={i}>
                      <CheckCircle2 size={16} className="jm-deliverable-icon" />
                      <div className="jm-deliverable-fields">
                        <input
                          className="jm-input jm-input-inline"
                          value={d.label}
                          onChange={e => updateDeliverable(i, 'label', e.target.value)}
                          placeholder="Deliverable name"
                        />
                        <input
                          className="jm-input jm-input-inline jm-input-muted"
                          value={d.description}
                          onChange={e => updateDeliverable(i, 'description', e.target.value)}
                          placeholder="Brief description"
                        />
                      </div>
                      <button
                        className="jm-icon-btn jm-icon-btn-danger"
                        onClick={() => removeDeliverable(i)}
                        title="Remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Next Stage Teaser ──────────────────────────────────── */}
              {selectedStage < 6 && (
                <div className="jm-card">
                  <div className="jm-card-header">
                    <ChevronDown size={16} />
                    <h3>Next Stage Teaser</h3>
                  </div>
                  <div className="jm-card-body">
                    <div className="jm-field">
                      <input
                        className="jm-input"
                        value={editStage.nextStageTeaser || ''}
                        onChange={e => updateStageField('nextStageTeaser', e.target.value)}
                        placeholder="A line that builds anticipation for the next stage…"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Trust Stats Tab ──────────────────────────────────────────── */}
        {activeTab === 'stats' && (
          <div className="jm-single-panel">
            <div className="jm-card">
              <div className="jm-card-header">
                <BarChart3 size={16} />
                <h3>Trust Statistics</h3>
                <span className="jm-card-hint">Displayed on the hero and footer</span>
              </div>
              <div className="jm-card-body">
                <div className="jm-stat-grid">
                  <div className="jm-field">
                    <label className="jm-label">
                      <Users size={14} />
                      Families Served
                    </label>
                    <input
                      className="jm-input"
                      type="number"
                      value={editStats.familiesServed}
                      onChange={e => setEditStats(prev => prev ? { ...prev, familiesServed: parseInt(e.target.value) || 0 } : null)}
                    />
                  </div>
                  <div className="jm-field">
                    <label className="jm-label">
                      <Star size={14} />
                      Average Rating
                    </label>
                    <input
                      className="jm-input"
                      type="number"
                      step="0.1"
                      min="0"
                      max="5"
                      value={editStats.averageRating}
                      onChange={e => setEditStats(prev => prev ? { ...prev, averageRating: parseFloat(e.target.value) || 0 } : null)}
                    />
                  </div>
                  <div className="jm-field">
                    <label className="jm-label">
                      <MessageSquare size={14} />
                      Review Count
                    </label>
                    <input
                      className="jm-input"
                      type="number"
                      value={editStats.reviewCount}
                      onChange={e => setEditStats(prev => prev ? { ...prev, reviewCount: parseInt(e.target.value) || 0 } : null)}
                    />
                  </div>
                  <div className="jm-field">
                    <label className="jm-label">
                      <Clock size={14} />
                      Years in Business
                    </label>
                    <input
                      className="jm-input"
                      type="number"
                      value={editStats.yearsInBusiness}
                      onChange={e => setEditStats(prev => prev ? { ...prev, yearsInBusiness: parseInt(e.target.value) || 0 } : null)}
                    />
                  </div>
                  <div className="jm-field">
                    <label className="jm-label">
                      <MapPin size={14} />
                      States Served
                    </label>
                    <input
                      className="jm-input"
                      type="number"
                      value={editStats.statesServed}
                      onChange={e => setEditStats(prev => prev ? { ...prev, statesServed: parseInt(e.target.value) || 0 } : null)}
                    />
                  </div>
                </div>
              </div>
              <div className="jm-card-footer">
                <button
                  className="jm-btn jm-btn-save"
                  onClick={handleSaveStats}
                  disabled={saving}
                >
                  {saving ? <Loader2 size={15} className="jm-spin" /> : <Save size={15} />}
                  <span>Save Stats</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Media Tab ─────────────────────────────────────────────────── */}
        {activeTab === 'media' && (
          <div className="jm-single-panel">
            <div className="jm-card">
              <div className="jm-card-header">
                <Film size={16} />
                <h3>Stage Media & Models</h3>
                <span className="jm-card-hint">Videos and 3D model shown by scroll stage on /journey</span>
              </div>
              <div className="jm-card-body">
                <MediaUploadField
                  label="Drone Video (Stages 3–4)"
                  hint="Keyframed drone video for design/site"
                  value={editMedia?.droneDesign ?? ''}
                  onChange={(url) => { setEditMedia(prev => ({ ...(prev || {}), droneDesign: url })); setHasUnsavedChanges(true); }}
                  onUpload={(file) => uploadJourneyMedia(file, 'media/drone-design')}
                  onUploadComplete={(url) => {
                    const updated = { ...(editMedia || {}), droneDesign: url };
                    setEditMedia(updated);
                    saveJourneyMedia(updated, user?.id, user?.name || user?.email)
                      .then(() => { setHasUnsavedChanges(false); showSaved('Media saved'); })
                      .catch(() => showSaved('Save failed — try again'));
                  }}
                  accept="video/*"
                />
                <MediaUploadField
                  label="CNC Machine Video (Stage 6)"
                  hint="Animation of CNC machine in production"
                  value={editMedia?.cnc ?? ''}
                  onChange={(url) => { setEditMedia(prev => ({ ...(prev || {}), cnc: url })); setHasUnsavedChanges(true); }}
                  onUpload={(file) => uploadJourneyMedia(file, 'media/cnc')}
                  onUploadComplete={(url) => {
                    const updated = { ...(editMedia || {}), cnc: url };
                    setEditMedia(updated);
                    saveJourneyMedia(updated, user?.id, user?.name || user?.email)
                      .then(() => { setHasUnsavedChanges(false); showSaved('Media saved'); })
                      .catch(() => showSaved('Save failed — try again'));
                  }}
                  accept="video/*"
                />
                <MediaUploadField
                  label="Crew Raising Video (Stage 7)"
                  hint="Drone video of crew raising the frame"
                  value={editMedia?.crewRaising ?? ''}
                  onChange={(url) => { setEditMedia(prev => ({ ...(prev || {}), crewRaising: url })); setHasUnsavedChanges(true); }}
                  onUpload={(file) => uploadJourneyMedia(file, 'media/crew-raising')}
                  onUploadComplete={(url) => {
                    const updated = { ...(editMedia || {}), crewRaising: url };
                    setEditMedia(updated);
                    saveJourneyMedia(updated, user?.id, user?.name || user?.email)
                      .then(() => { setHasUnsavedChanges(false); showSaved('Media saved'); })
                      .catch(() => showSaved('Save failed — try again'));
                  }}
                  accept="video/*"
                />
                <ModelUploadField
                  label="3D Model (Stages 1–2)"
                  hint="GLB model for concept/quote stages"
                  value={editMedia?.model3d ?? ''}
                  onChange={(url) => { setEditMedia(prev => ({ ...(prev || {}), model3d: url })); setHasUnsavedChanges(true); }}
                  onUpload={(file) => uploadJourneyMedia(file, 'models/stages-1-2')}
                  onUploadComplete={(url) => {
                    const updated = { ...(editMedia || {}), model3d: url };
                    setEditMedia(updated);
                    saveJourneyMedia(updated, user?.id, user?.name || user?.email)
                      .then(() => { setHasUnsavedChanges(false); showSaved('Media saved'); })
                      .catch(() => showSaved('Save failed — try again'));
                  }}
                />
                <ModelUploadField
                  label="3D Model (Stage 5 – Engineering)"
                  hint="GLB model for 2D plans / engineering stage"
                  value={editMedia?.model3dEngineering ?? ''}
                  onChange={(url) => { setEditMedia(prev => ({ ...(prev || {}), model3dEngineering: url })); setHasUnsavedChanges(true); }}
                  onUpload={(file) => uploadJourneyMedia(file, 'models/stage-5')}
                  onUploadComplete={(url) => {
                    const updated = { ...(editMedia || {}), model3dEngineering: url };
                    setEditMedia(updated);
                    saveJourneyMedia(updated, user?.id, user?.name || user?.email)
                      .then(() => { setHasUnsavedChanges(false); showSaved('Media saved'); })
                      .catch(() => showSaved('Save failed — try again'));
                  }}
                />
              </div>
              <div className="jm-card-footer">
                <button
                  className="jm-btn jm-btn-save"
                  onClick={handleSaveMedia}
                  disabled={saving}
                >
                  {saving ? <Loader2 size={15} className="jm-spin" /> : <Save size={15} />}
                  <span>Save Media</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Settings Tab ─────────────────────────────────────────────── */}
        {activeTab === 'settings' && (
          <div className="jm-single-panel">
            <div className="jm-card">
              <div className="jm-card-header">
                <Settings size={16} />
                <h3>Page Settings</h3>
              </div>
              <div className="jm-card-body">
                <div className="jm-field">
                  <label className="jm-label">CTA Button URL</label>
                  <input
                    className="jm-input"
                    value={editSettings.ctaUrl || ''}
                    onChange={e => setEditSettings(prev => prev ? { ...prev, ctaUrl: e.target.value } : null)}
                    placeholder="https://www.lctimberframes.com/contact"
                  />
                </div>
                <div className="jm-field">
                  <label className="jm-label">CTA Button Label</label>
                  <input
                    className="jm-input"
                    value={editSettings.ctaLabel || ''}
                    onChange={e => setEditSettings(prev => prev ? { ...prev, ctaLabel: e.target.value } : null)}
                    placeholder="Speak with the Founder"
                  />
                </div>

                <div className="jm-settings-status">
                  <div className={`jm-publish-status ${data.settings.isPublished ? 'is-live' : 'is-draft'}`}>
                    {data.settings.isPublished ? <Globe size={16} /> : <GlobeLock size={16} />}
                    <div>
                      <strong>{data.settings.isPublished ? 'Published' : 'Draft'}</strong>
                      {data.settings.lastPublishedAt && (
                        <span>Last published {new Date(data.settings.lastPublishedAt).toLocaleDateString()} by {data.settings.lastPublishedBy}</span>
                      )}
                    </div>
                  </div>
                  <a
                    href="/journey"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="jm-btn jm-btn-ghost"
                  >
                    <ExternalLink size={15} />
                    <span>Open /journey</span>
                  </a>
                </div>
              </div>
              <div className="jm-card-footer">
                <button
                  className="jm-btn jm-btn-save"
                  onClick={handleSaveSettings}
                  disabled={saving}
                >
                  {saving ? <Loader2 size={15} className="jm-spin" /> : <Save size={15} />}
                  <span>Save Settings</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JourneyManagement;
